import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Coins, ArrowLeftRight, Copy, TrendingUp, TrendingDown, Minus, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { STORAGE_BASE } from '../config';
import ImageFallback from '../components/ImageFallback';
import { partStyle } from '../components/widgetStyle';
import { ICONS } from './WidgetIcons';
import { CommentsBox } from '../components/CommentsBox';
import TemplateList from './ListTemplate';
import { Accordion } from '../components/ui/accordion';
import { priceInfo } from '../lib/price';

export const resolveSrc = p => {
  if (!p) return '';
  if (/^https?:\/\//.test(p)) return p;
  return `${STORAGE_BASE}/${String(p).replace(/^\/+/, '')}`;
};

function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const f = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return m;
}

/** Text widget */
export function WidgetText({ settings = {} }) {
  const s = settings;
  const mobile = useIsMobile();
  if (!s.content) return null;
  const fontSize = (mobile && s.fontSizeMobile) || s.fontSize;
  return (
    <div style={{
      background: s.bg || 'transparent',
      color: s.color || 'inherit',
      textAlign: s.align || 'right',
      fontSize: fontSize ? `${fontSize}px` : undefined,
      fontWeight: s.bold ? 700 : undefined,
      padding: s.padding ? `${s.padding}px` : undefined,
      borderRadius: s.radius ? `${s.radius}px` : undefined,
      lineHeight: 2,
      whiteSpace: 'pre-line',
      overflowWrap: 'break-word',
      maxWidth: '100%',
    }}>
      {s.content}
    </div>
  );
}

/** Image widget (optional link) */
export function WidgetImage({ settings = {} }) {
  const s = settings;
  const mobile = useIsMobile();
  const src = resolveSrc(s.image);
  if (!src) return <ImageFallback label={s.alt || s.title || 'تصویر فروشگاه'} seed={s.alt || s.title} style={{ width: '100%', minHeight: 180, borderRadius: 'var(--shape-card, 16px)' }} />;
  const width = (mobile && s.widthMobile) || s.width || 100;
  const hUnit = s.imgHeightUnit || 'px';
  const hRaw = s.imgHeight;
  const hasH = hRaw !== undefined && hRaw !== '' && hRaw !== null && Number(hRaw) > 0;
  const img = (
    <img
      src={src}
      alt={s.alt || ''}
      loading="lazy"
      style={{
        width: `${width}%`,
        ...(hasH ? (hUnit === '%'
          ? { aspectRatio: `100 / ${Number(hRaw)}`, objectFit: 'cover' }
          : { height: `${Number(hRaw)}px`, objectFit: 'cover' }) : {}),
        borderRadius: s.radius ? `${s.radius}px` : undefined,
        display: 'block',
        margin: s.align === 'center' ? '0 auto' : s.align === 'left' ? '0 auto 0 0' : '0 0 0 auto',
        maxWidth: '100%',
      }}
    />
  );
  if (s.link) {
    const external = /^https?:\/\//.test(s.link);
    return <a href={s.link} {...(external ? { target: '_blank', rel: 'noreferrer' } : {})} style={{ display: 'block' }}>{img}</a>;
  }
  return img;
}

/** Custom image slider widget */
export function WidgetSlider({ settings = {} }) {
  const slides = (settings.slides || []).filter(sl => sl.image);
  const mobile = useIsMobile();
  const [i, setI] = useState(0);
  const interval = settings.interval || 5000;
  const width = (mobile && settings.widthMobile) || settings.width || 100;
  const hUnit = (mobile ? (settings.heightMobileUnit || settings.heightUnit) : settings.heightUnit) || 'px';
  const hRaw = (mobile && settings.heightMobile !== undefined && settings.heightMobile !== '' && settings.heightMobile !== null)
    ? settings.heightMobile : (settings.height ?? 380);
  const hVal = Number(hRaw) || 380;
  const boxHeight = hUnit === '%' ? undefined : hVal;
  const boxRatio = hUnit === '%' ? `100 / ${hVal}` : undefined;

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setI(p => (p + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval]);

  if (!slides.length) return null;
  const radius = settings.radius ?? 16;

  return (
    <div style={{ position: 'relative', width: `${width}%`, maxWidth: '100%', margin: '0 auto', height: boxHeight, aspectRatio: boxRatio, overflow: 'hidden', borderRadius: radius, background: 'var(--muted)', border: settings.bordered === false ? 'none' : 'var(--shape-line) solid var(--border)' }}>
      {slides.map((sl, idx) => {
        const src = resolveSrc(sl.image);
        const img = src ? (
          <img key={sl.image} src={src} alt={sl.title || ''} loading={idx === 0 ? 'eager' : 'lazy'}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : null;
        return (
          <div key={idx} style={{ position: 'absolute', inset: 0, opacity: idx === i ? 1 : 0, transition: 'opacity 0.8s ease', pointerEvents: idx === i ? 'auto' : 'none' }}>
            {sl.link ? <a href={sl.link} style={{ display: 'block', width: '100%', height: '100%' }}>{img}</a> : img}
          </div>
        );
      })}
      {(slides[i].title || slides[i].subtitle) && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(12,10,9,0.55) 0%, rgba(12,10,9,0.15) 55%, transparent 100%)', pointerEvents: 'none' }} />
      )}
      {(slides[i].title || slides[i].subtitle) && (
        <div style={{ position: 'absolute', bottom: '12%', right: '6%', left: '6%', color: '#fff', textAlign: 'right' }}>
          {slides[i].title && <h1 style={{ fontFamily: 'inherit', fontSize: settings.slideSize ? `${settings.slideSize}px` : 'clamp(1.2rem, 3.5vw, 1.8rem)', color: settings.slideColor || '#fff', fontWeight: 700, margin: 0, textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>{slides[i].title}</h1>}
          {slides[i].subtitle && <div style={{ fontSize: '0.9rem', marginTop: 4, opacity: 0.9 }}>{slides[i].subtitle}</div>}
        </div>
      )}
      {slides.length > 1 && (
        <div style={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
          {slides.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} aria-label={`اسلاید ${idx + 1}`}
              style={{ width: idx === i ? 26 : 9, height: 9, borderRadius: 5, border: 'none', background: idx === i ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'width 0.3s', cursor: 'pointer', padding: 0 }} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Custom FAQ widget (falls back to global FAQs) — vibefarsi accordion language. */
export function WidgetFaq({ settings = {}, data }) {
  const items = (settings.items && settings.items.length ? settings.items : (data.faqs || []).map(f => ({ q: f.question, a: f.answer })));
  if (!items.length) return null;
  return (
    <div>
      {(settings.sectionTitle || settings.title) && (
        <h2 style={{ textAlign: 'center', fontWeight: 800, fontSize: settings.titleSize ? `${settings.titleSize}px` : 'clamp(1.3rem, 3.5vw, 1.8rem)', margin: '0 0 20px', color: settings.titleColor || 'inherit' }}>
          {settings.sectionTitle || settings.title}
        </h2>
      )}
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <Accordion
          defaultOpen={['q0']}
          items={items.map((f, i) => ({
            id: 'q' + i,
            title: <span style={{ fontWeight: 600, fontSize: settings.qSize ? `${settings.qSize}px` : '0.95rem', color: settings.qColor || 'inherit' }}>{f.q}</span>,
            content: <span style={{ color: settings.aColor || 'var(--muted-fg)', lineHeight: 1.9 }}>{f.a}</span>,
          }))}
        />
      </div>
    </div>
  );
}

/** Icon widget */
export function WidgetIcon({ settings = {} }) {
  const s = settings;
  const Cmp = ICONS[s.icon] || ICONS.Sparkles;
  const size = s.size || 32;
  const el = (
    <span style={{
      display: 'inline-flex', lineHeight: 0,
      padding: s.bg && s.bg !== 'transparent' ? Math.round(size * 0.35) : 0,
      background: s.bg && s.bg !== 'transparent' ? s.bg : 'transparent',
      borderRadius: s.radius ?? '50%',
      color: s.color || 'inherit',
    }}>
      <Cmp size={size} />
    </span>
  );
  const wrap = (
    <div style={{ textAlign: s.align || 'center' }}>
      {s.link ? <a href={s.link} style={{ display: 'inline-block' }}>{el}</a> : el}
    </div>
  );
  return wrap;
}

/** Divider widget */
export function WidgetDivider({ settings = {} }) {
  const s = settings;
  const color = s.color || 'var(--border)';
  const t = s.thickness || 1;
  // Line model lives in lineStyle; legacy widgets stored it in the `style` string key.
  const model = s.lineStyle || (typeof s.style === 'string' ? s.style : 'solid');
  const line = model === 'gradient'
    ? { height: t, background: `linear-gradient(90deg, transparent, ${color}, transparent)`, border: 'none' }
    : { borderTop: `${t}px ${model || 'solid'} ${color}`, height: 0 };
  return (
    <div style={{ width: `${s.width ?? 100}%`, margin: `${s.gap ?? 16}px auto`, textAlign: s.align || 'center' }}>
      <div style={{ ...line, width: '100%', margin: 0 }} />
    </div>
  );
}

/** Map widget */
export function WidgetMap({ settings = {} }) {
  const s = settings;
  const src = s.embedUrl || (s.address ? `https://maps.google.com/maps?q=${encodeURIComponent(s.address)}&t=&z=13&ie=UTF8&iwloc=&output=embed` : '');
  if (!src) return null;
  return (
    <iframe
      title={s.address || 'map'}
      src={src}
      loading="lazy"
      style={{ width: '100%', height: s.height || 320, border: 0, borderRadius: s.radius ?? 16, display: 'block', maxWidth: '100%' }}
    />
  );
}

/** Custom HTML widget */
export function WidgetHtml({ settings = {} }) {
  if (!settings.html) return null;
  const html = settings.html;
  // ExploreGrid widget: auto-rotating instagram-style grid, images pipe-separated
  if (html.includes('data-explore-grid')) {
    return <ExploreGrid settings={settings} />;
  }
  return <div style={{ maxWidth: '100%', overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: settings.html }} />;
}

/** Auto-rotating instagram-explore grid (gemini-designed): irregular tiles + fade rotation. */
function ExploreGrid({ settings = {} }) {
  const raw = settings.html || '';
  const m = raw.match(/data-explore-grid="([^"]*)"/);
  const images = (m ? m[1] : '').split('|').map(s => s.trim()).filter(Boolean).map(p =>
    /^https?:\/\//.test(p) ? p : `${STORAGE_BASE}/${String(p).replace(/^\/+/, '')}`);
  const layouts = [
    { gc: 'span 1', gr: 'span 2' }, { gc: 'span 1', gr: 'span 1' },
    { gc: 'span 1', gr: 'span 1' }, { gc: 'span 1', gr: 'span 2' },
    { gc: 'span 1', gr: 'span 1' }, { gc: 'span 1', gr: 'span 1' },
  ];
  const [offset] = useState(0);
  if (!images.length) return null;
  return (
    <div>
      {(settings.sectionTitle || settings.title) && (
        <h2 style={{ fontWeight: 700, fontSize: 'clamp(1.2rem, 3.5vw, 1.7rem)', margin: '0 0 12px', textAlign: 'center' }}>{settings.sectionTitle || settings.title}</h2>
      )}
      <div dir="rtl" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'minmax(110px, auto)', gridAutoFlow: 'dense', gap: 6, width: '100%' }}>
        {layouts.slice(0, images.length).map((l, i) => (
          <div key={i} style={{ overflow: 'hidden', borderRadius: 'var(--shape-card)', background: 'var(--muted)', gridColumn: l.gc, gridRow: l.gr }}>
            <img src={images[(i + offset) % images.length]} alt="" loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Video widget with native player */
export function WidgetVideo({ settings = {} }) {
  const s = settings;
  const src = resolveSrc(s.video);
  if (!src) return null;
  return (
    <video
      src={src}
      controls={s.controls !== false}
      autoPlay={!!s.autoplay}
      muted={!!(s.autoplay || s.muted)}
      loop={!!s.loop}
      playsInline
      preload="metadata"
      poster={resolveSrc(s.poster) || undefined}
      style={{ width: '100%', display: 'block', borderRadius: s.radius ?? 16, background: '#000', maxWidth: '100%' }}
    />
  );
}

/** Icon + text widget */
export function WidgetIconText({ settings = {} }) {
  const s = settings;
  const Cmp = ICONS[s.icon] || ICONS.Sparkles;
  const size = s.iconSize || 28;
  const side = s.layout === 'side';
  return (
    <div style={{
      display: 'flex', flexDirection: side ? 'row' : 'column',
      alignItems: side ? 'flex-start' : 'center', gap: 12,
      textAlign: s.align || (side ? 'right' : 'center'),
      background: s.bg && s.bg !== 'transparent' ? s.bg : 'var(--card)',
      border: 'var(--card-line, var(--shape-line) solid var(--border))',
      borderRadius: 'var(--shape-card)',
      boxShadow: 'var(--card-shadow-i, none)',
      backdropFilter: 'var(--surface-blur-i, none)',
      padding: s.padding ? `${s.padding}px` : '20px 18px',
    }}>
      <span style={{
        display: 'inline-flex', lineHeight: 0, flexShrink: 0,
        padding: Math.round(size * 0.35),
        background: s.iconBg && s.iconBg !== 'transparent' ? s.iconBg : 'var(--icon-bg, color-mix(in srgb, var(--primary) 14%, transparent))',
        borderRadius: 'var(--icon-radius, 14px)', color: s.iconColor || 'var(--primary)',
        ...partStyle(s.style, 'icon'),
      }}>
        <Cmp size={size} />
      </span>
      <div style={{ minWidth: 0 }}>
        {s.title && <div style={{ fontWeight: 'var(--title-weight, 700)', fontSize: s.titleSize ? `${s.titleSize}px` : '1rem', color: s.titleColor || 'var(--card-fg)', marginBottom: s.text ? 4 : 0, ...partStyle(s.style, 'title') }}>{s.title}</div>}
        {s.text && <div style={{ fontSize: '0.88rem', lineHeight: 1.9, color: s.textColor || 'var(--muted-fg)', whiteSpace: 'pre-line', ...partStyle(s.style, 'desc') }}>{s.text}</div>}
      </div>
    </div>
  );
}

/** Data table widget (headers comma-separated, each row on a line, cells split by |) */
export function WidgetTable({ settings = {} }) {
  const s = settings;
  const headers = String(s.headers || '').split(',').map(h => h.trim()).filter(Boolean);
  const rows = String(s.rows_text || '').split('\n').map(l => l.trim()).filter(Boolean)
    .map(l => l.includes('|') ? l.split('|').map(c => c.trim()) : [l]);
  const nCols = Math.max(headers.length, 1);
  if (!headers.length && !rows.length) return null;
  const bc = s.borderColor || 'var(--border)';
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', border: `var(--shape-line) solid ${bc}`, borderRadius: 'var(--shape-card)', overflow: 'hidden', fontSize: s.fontSize ? `${s.fontSize}px` : '0.85rem', textAlign: s.align || 'right' }}>
        {headers.length > 0 && (
          <thead><tr style={{ background: s.headerBg || 'var(--primary)', color: s.headerColor || 'var(--on-primary)', fontWeight: 700 }}>
            {headers.map((h, i) => <th key={i} style={{ padding: '10px 12px', borderBottom: `1.5px solid ${bc}` }}>{h}</th>)}
          </tr></thead>
        )}
        <tbody>
          {rows.map((cells, i) => {
            const padded = cells.slice(0, nCols).concat(Array(Math.max(0, nCols - cells.length)).fill(''));
            return (
              <tr key={i} style={{ background: i % 2 && s.striped !== false ? (s.stripeBg || 'var(--muted)') : 'transparent' }}>
                {padded.map((c, j) => <td key={j} style={{ padding: '10px 12px', borderTop: `1px solid ${s.rowBorder || 'var(--border)'}`, color: s.bodyColor || 'inherit' }}>{c}</td>)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Live rates widget: tab bar of purities/currencies + live two-way converter */
export function WidgetPrice({ settings = {} }) {
  const s = settings;
  const [data, setData] = useState(null);
  const [selId, setSelId] = useState('');
  const [toman, setToman] = useState('');
  const [gram, setGram] = useState('');
  const [last, setLast] = useState('toman');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const fetchRates = () => {
    setLoading(true);
    axios.get('/rates').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchRates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const all = data?.items || [];
  const items = (s.itemIds && s.itemIds.length) ? all.filter(i => s.itemIds.includes(i.id)) : all;
  const sel = items.find(i => i.id === selId) || items[0];
  const price = Number(sel?.price || 0);
  const mode = s.converter || 'both';
  const fa = n => Number(n || 0).toLocaleString('fa-IR');
  const tabActiveBg = (s.tabActiveBg && s.tabActiveBg !== 'transparent') ? s.tabActiveBg : 'var(--primary)';
  const tabActiveColor = s.tabActiveColor || 'var(--on-primary, #fff)';
  const tabInactiveBg = (s.tabInactiveBg && s.tabInactiveBg !== 'transparent') ? s.tabInactiveBg : 'var(--card)';
  const tabInactiveColor = s.tabInactiveColor || 'var(--muted-fg, #52525B)';
  const panelBg = s.panelBg || 'var(--card)';
  const panelBorder = s.panelBorder || 'var(--border)';
  const priceColor = s.priceColor || 'var(--accent)';
  const glass = s.glassStyle !== false;
  const trend = s.showTrend !== false;
  const pick = id => { setSelId(id); setToman(''); setGram(''); setLast('toman'); };
  const copyPrice = async () => {
    try { await navigator.clipboard.writeText(String(price)); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  // live two-way values
  const gramShown = last === 'toman' ? (toman === '' ? '' : (Number(toman || 0) / (price || 1)).toFixed(3)) : gram;
  const tomanShown = last === 'gram' ? (gram === '' ? '' : String(Math.round(Number(gram || 0) * price))) : toman;
  const quicks = [1000000, 5000000, 10000000];
  const inStyle = { width: '100%', flex: 1, minWidth: 0, padding: '12px 14px', minHeight: 'var(--shape-input-h)', boxSizing: 'border-box', borderRadius: 'var(--shape-input)', border: `var(--shape-line-input) solid ${panelBorder}`, fontSize: '0.9rem', outline: 'none', background: 'var(--card)', color: 'var(--card-fg, inherit)', fontFamily: 'inherit', textAlign: 'center', boxSizing: 'border-box' };
  return (
    <div>
      {((s.sectionTitle || s.title)) && (
        <h2 style={{ fontWeight: 800, fontSize: s.titleSize ? `${s.titleSize}px` : 'clamp(1.2rem, 3.5vw, 1.7rem)', margin: '0 0 4px', color: s.titleColor || 'inherit', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <Coins size={22} style={{ color: priceColor }} />{(s.sectionTitle || s.title)}
        </h2>
      )}
      {data?.updated_at && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--muted-fg, #A1A1AA)', marginBottom: 10 }}>
          <span className="rates-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok, #22C55E)', display: 'inline-block' }} />
          قیمت زنده
          <button onClick={fetchRates} title="به‌روزرسانی" aria-label="به‌روزرسانی"
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted-fg)', display: 'inline-flex', padding: 2 }}>
            <RefreshCw size={13} style={{ animation: loading ? 'rates-spin 1s linear infinite' : 'none' }} />
          </button>
          <style>{`@keyframes rates-spin { to { transform: rotate(360deg); } } .rates-pulse { animation: rates-pulse 2s ease-in-out infinite; } @keyframes rates-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }`}</style>
        </div>
      )}
      {items.length > 1 ? (
        <div className="rates-tabs" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, direction: 'rtl' }}>
          {items.map(it => {
            const active = sel?.id === it.id;
            return (
              <button key={it.id} onClick={() => pick(it.id)}
                style={{
                  flex: '1 0 auto', padding: '10px 18px', minHeight: 'var(--shape-btn-h)', borderRadius: 999,
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: '0.88rem', fontWeight: active ? 700 : 500, whiteSpace: 'nowrap',
                  background: active ? tabActiveBg : tabInactiveBg, color: active ? tabActiveColor : tabInactiveColor,
                  boxShadow: active ? '0 4px 14px rgba(0,0,0,0.16)' : 'none',
                  transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
                }}>
                {it.title}
              </button>
            );
          })}
        </div>
      ) : null}
      <div style={{ border: s.noBorder === true ? 'none' : `var(--shape-line) solid ${panelBorder}`, borderRadius: 'var(--shape-card)', overflow: 'hidden', background: panelBg, marginTop: items.length > 1 ? 8 : 0, ...(glass ? { backdropFilter: 'blur(16px) saturate(160%)', WebkitBackdropFilter: 'blur(16px) saturate(160%)', boxShadow: '0 12px 40px rgba(69,26,3,0.10), inset 0 1px 0 rgba(255,255,255,0.55)' } : {}) }}>
        {sel && (
          <div style={{ padding: '14px 16px 12px', textAlign: 'center', direction: 'rtl', background: 'radial-gradient(ellipse at center, var(--muted) 0%, transparent 72%)' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--muted-fg, #A1A1AA)', marginBottom: 6 }}>قیمت هر {sel.unit || 'گرم'} {sel.title}</div>
            <div style={{
              fontWeight: 900, fontSize: 'clamp(1.8rem, 6vw, 2.6rem)', lineHeight: 1.4,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              ...(s.priceColor
                ? { color: priceColor }
                : { backgroundImage: 'linear-gradient(135deg, var(--primary), var(--accent))', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }),
            }}>
              {loading ? '...' : price > 0 ? <>{fa(price)} <span style={{ fontSize: '0.45em', fontWeight: 600 }}>تومان</span></> : '—'}
            </div>
          </div>
        )}
        {!items.length && <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted-fg)', fontSize: '0.85rem' }}>نرخی ثبت نشده است.</div>}
        {mode !== 'none' && price > 0 && sel && (
          <div style={{ borderTop: s.noBorder === true ? 'none' : `1px solid ${panelBorder}`, padding: 12, background: 'transparent' }}>
            <div className={mode === 'both' ? 'rates-conv' : 'rates-conv single'}>
              {(mode === 'toman' || mode === 'both') && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--muted-fg)', marginBottom: 4, textAlign: 'center' }}>{last === 'gram' ? (sel.unit || 'گرم') : 'تومان'}</label>
                  <input value={tomanShown} onChange={e => { setToman(e.target.value.replace(/[^0-9]/g, '')); setLast('toman'); }}
                    placeholder="۰" style={inStyle} inputMode="numeric" />
                </div>
              )}
              {mode === 'both' && (
                <button onClick={() => { setLast(l => (l === 'toman' ? 'gram' : 'toman')); }}
                  title="جابه‌جایی جهت" aria-label="جابه‌جایی جهت"
                  style={{ border: 'var(--shape-line) solid var(--border)', background: 'var(--card)', borderRadius: 'var(--shape-btn)', width: 36, height: 36, cursor: 'pointer', color: priceColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, alignSelf: 'end', marginBottom: 2 }}>
                  <ArrowLeftRight size={16} />
                </button>
              )}
              {(mode === 'gram' || mode === 'both') && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--muted-fg)', marginBottom: 4, textAlign: 'center' }}>{last === 'gram' ? 'تومان' : (sel.unit || 'گرم')}</label>
                  <input value={gramShown} onChange={e => { setGram(e.target.value.replace(/[^0-9.]/g, '')); setLast('gram'); }}
                    placeholder="۰" style={inStyle} inputMode="decimal" />
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--muted-fg)', marginTop: 10 }}>
              {mode === 'both'
                ? (last === 'toman'
                  ? (toman === '' ? 'مبلغ یا وزن را وارد کنید' : <><b>{fa(tomanShown)}</b> تومان ≈ <b>{gramShown}</b> {sel.unit || 'گرم'} {sel.title}</>)
                  : (gram === '' ? 'مبلغ یا وزن را وارد کنید' : <><b>{gramShown}</b> {sel.unit || 'گرم'} {sel.title} ≈ <b>{fa(tomanShown)}</b> تومان</>))
                : mode === 'toman'
                  ? (toman === '' ? 'مبلغ را وارد کنید' : <><b>{fa(tomanShown)}</b> تومان ≈ <b>{gramShown}</b> {sel.unit || 'گرم'}</>)
                  : (gram === '' ? 'وزن را وارد کنید' : <><b>{gramShown}</b> {sel.unit || 'گرم'} ≈ <b>{fa(tomanShown)}</b> تومان</>)}
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10, flexWrap: 'wrap' }}>
              {quicks.map(q => (
                <button key={q} onClick={() => { setToman(String(q)); setLast('toman'); }}
                  style={{ border: 'var(--shape-line) solid var(--border)', background: 'var(--card)', borderRadius: 'var(--shape-btn)', padding: '4px 14px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', color: 'var(--muted-fg)', fontFamily: 'inherit' }}>
                  {fa(q)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {data?.updated_at && <div style={{ fontSize: '0.7rem', color: 'var(--muted-fg)', marginTop: 6, textAlign: 'center' }}>آخرین به‌روزرسانی: {data.updated_at}</div>}
    </div>
  );
}

/** Blog post list (self-fetching, template-driven) */
export function WidgetPostList({ settings = {} }) {
  const s = settings;
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    axios.get('/posts', { params: { per_page: s.limit || 6 } }).then(r => setPosts(r.data?.data || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <TemplateList kind="post" items={posts} settings={s} />;
}

/** Current post meta (title/cover/date) — needs data.post context */
export function WidgetPostMeta({ settings = {}, ctx = {} }) {
  const s = settings;
  const post = ctx.post;
  if (!post) return null;
  return (
    <div style={{ textAlign: 'center' }}>
      {s.showTitle !== false && (
        <h1 style={{ fontWeight: 800, fontSize: s.titleSize ? `${s.titleSize}px` : 'clamp(1.4rem, 4vw, 2rem)', margin: '0 0 10px', color: s.titleColor || 'inherit', lineHeight: 1.6 }}>{post.title}</h1>
      )}
      {s.showDate !== false && post.published_at && (
        <div style={{ fontSize: '0.8rem', color: 'var(--muted-fg)', marginBottom: 12 }}>{new Date(post.published_at).toLocaleDateString('fa-IR')}</div>
      )}
      {s.showCover !== false && post.cover && (
        <img src={(/^https?:\/\//.test(post.cover) ? post.cover : `${STORAGE_BASE}/${String(post.cover).replace(/^\/+/, '')}`)} alt={post.title} style={{ width: '100%', maxHeight: 420, objectFit: 'cover', borderRadius: 'var(--shape-card)', display: 'block', background: 'var(--muted)' }} />
      )}
      {s.showExcerpt === true && post.excerpt && (
        <p style={{ fontSize: '0.95rem', color: 'var(--muted-fg)', lineHeight: 2, marginTop: 12 }}>{post.excerpt}</p>
      )}
    </div>
  );
}

/** Mini cart widget for pages (cart_widget): live cart summary + CTA */
export function WidgetCart({ settings = {} }) {
  const s = settings;
  const [items, setItems] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  useEffect(() => {
    let t = null;
    try { t = localStorage.getItem('customer_token'); } catch {}
    if (!t) { setLoggedIn(false); setItems([]); return; }
    setLoggedIn(true);
    axios.get('/cart', { headers: { Authorization: 'Bearer ' + t } })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);
  const fa = n => Number(n || 0).toLocaleString('fa-IR');
  const lines = items || [];
  const total = lines.reduce((a, c) => a + Number(c.unit_price ?? c.product?.customization_data?.base_price ?? c.product?.price ?? 0) * (c.qty || 1), 0);
  const count = lines.reduce((a, c) => a + (c.qty || 1), 0);
  return (
    <div style={{ border: 'var(--shape-line) solid var(--border, #E4E4E7)', borderRadius: 'var(--shape-card)', padding: 18, background: 'var(--card, #fff)', maxWidth: 560, margin: '0 auto' }}>
      {(s.sectionTitle || s.title) && (
        <div style={{ fontWeight: 800, fontSize: '1.05rem', marginBottom: 12, textAlign: 'center' }}>
          {(s.sectionTitle || s.title)} {count > 0 && <span style={{ color: 'var(--muted-fg)', fontWeight: 600, fontSize: '0.8rem' }}>({fa(count)} کالا)</span>}
        </div>
      )}
      {items === null ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ height: 18, borderRadius: 8, background: 'linear-gradient(90deg,var(--muted) 25%,color-mix(in srgb, var(--muted), var(--fg) 10%) 37%,var(--muted) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }} />
          <div style={{ height: 18, borderRadius: 8, width: '70%', background: 'linear-gradient(90deg,var(--muted) 25%,color-mix(in srgb, var(--muted), var(--fg) 10%) 37%,var(--muted) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }} />
        </div>
      ) : !loggedIn ? (
        <div style={{ textAlign: 'center', color: 'var(--muted-fg, #71717A)', fontSize: '0.88rem', lineHeight: 2 }}>
          برای مشاهده سبد خرید ابتدا <a href="/account" style={{ color: 'var(--accent, #A16207)', fontWeight: 700 }}>وارد شوید</a>.
        </div>
      ) : !lines.length ? (
        <div style={{ textAlign: 'center', color: 'var(--muted-fg)', fontSize: '0.9rem', padding: '12px 0' }}>سبد خرید شما خالی است.</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {lines.slice(0, 4).map((c, i) => {
              const unit = Number(c.unit_price ?? c.product?.customization_data?.base_price ?? c.product?.price ?? 0);
              return (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.85rem', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.product?.name || 'محصول'} <span style={{ color: 'var(--muted-fg)' }}>× {fa(c.qty || 1)}</span></span>
                  <b style={{ whiteSpace: 'nowrap' }}>{fa(unit * (c.qty || 1))} تومان</b>
                </div>
              );
            })}
            {lines.length > 4 && <div style={{ fontSize: '0.78rem', color: 'var(--muted-fg)', textAlign: 'center' }}>+ {fa(lines.length - 4)} قلم دیگر</div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--muted-fg)' }}>جمع سبد:</span>
            <b style={{ fontSize: '1.05rem' }}>{fa(total)} تومان</b>
          </div>
          {s.showWallet !== false && loggedIn && <WalletLine />}
          <a href="/cart" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', background: 'var(--primary, #18181B)', color: 'var(--on-primary, #fff)', minHeight: 'var(--shape-btn-h)', borderRadius: 'var(--shape-btn)', padding: '12px', fontWeight: 700, fontSize: '0.9rem' }}>
            مشاهده سبد و پرداخت
          </a>
        </>
      )}
    </div>
  );
}

function WalletLine() {
  const [bal, setBal] = useState(null);
  useEffect(() => {
    let t = null;
    try { t = localStorage.getItem('customer_token'); } catch {}
    if (!t) return;
    axios.get('/auth/me', { headers: { Authorization: 'Bearer ' + t } })
      .then(r => setBal(r.data?.user?.wallet_total ?? r.data?.wallet_total ?? null))
      .catch(() => {});
  }, []);
  if (bal === null) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: 'var(--ok, #15803D)', background: 'var(--muted)', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
      <span>موجودی کیف پول:</span><b>{Number(bal || 0).toLocaleString('fa-IR')} تومان</b>
    </div>
  );
}

/** Standalone form widget (form): loads an admin-designed form by id (plan feature: forms) */
export function WidgetForm({ settings = {} }) {
  const s = settings;
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const [failed, setFailed] = useState(false);
  const [allowed, setAllowed] = useState(null);
  useEffect(() => {
    axios.get('/site-settings').then(r => {
      const t = r.data && r.data.tenant;
      setAllowed(!t || (t.features || []).includes('forms'));
    }).catch(() => setAllowed(true));
  }, []);
  useEffect(() => {
    if (allowed === false) return;
    if (!s.form_id) return;
    setForm(null);
    axios.get(`/forms/${s.form_id}`).then(r => setForm(r.data)).catch(() => setFailed(true));
  }, [s.form_id, allowed]);
  if (allowed === false) return null;
  if (!s.form_id) return <div style={{ textAlign: 'center', color: 'var(--muted-fg)', fontSize: '0.85rem', padding: 16 }}>در تنظیمات ویجت یک فرم انتخاب کنید.</div>;
  if (failed) return null;
  if (!form) return (
    <div style={{ border: 'var(--shape-line) solid var(--border, #E4E4E7)', borderRadius: 'var(--shape-card)', padding: 18, maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ height: 24, width: '50%', margin: '0 auto', borderRadius: 8, background: 'linear-gradient(90deg,var(--muted) 25%,color-mix(in srgb, var(--muted), var(--fg) 10%) 37%,var(--muted) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }} />
      <div style={{ height: 44, borderRadius: 10, background: 'linear-gradient(90deg,var(--muted) 25%,color-mix(in srgb, var(--muted), var(--fg) 10%) 37%,var(--muted) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }} />
      <div style={{ height: 44, borderRadius: 10, background: 'linear-gradient(90deg,var(--muted) 25%,color-mix(in srgb, var(--muted), var(--fg) 10%) 37%,var(--muted) 63%)', backgroundSize: '400% 100%', animation: 'shimmer 1.4s ease infinite' }} />
    </div>
  );
  const set = (fid, v) => setAnswers(a => ({ ...a, [fid]: v }));
  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      let t = null;
      try { t = localStorage.getItem('customer_token'); } catch {}
      const r = await axios.post(`/forms/${form.id}/submit`, { answers },
        t ? { headers: { Authorization: 'Bearer ' + t } } : undefined);
      setDone(r.data.message || 'پاسخ شما ثبت شد.');
    } catch (err) {
      alert(err.response?.data?.message || 'خطا در ثبت پاسخ.');
    }
    setBusy(false);
  };
  const inStyle = { width: '100%', padding: '11px 14px', minHeight: 'var(--shape-input-h)', borderRadius: 'var(--shape-input)', border: 'var(--shape-line-input) solid var(--border)', fontSize: '0.88rem', outline: 'none', background: 'var(--card)', color: 'var(--fg)', fontFamily: 'inherit', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: 5 };
  if (done) {
    return (
      <div style={{ border: 'var(--shape-line) solid var(--border)', background: 'var(--muted)', borderRadius: 'var(--shape-card)', padding: 28, maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
        <div style={{ fontWeight: 700, color: 'var(--ok, #15803D)' }}>{done}</div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} style={{ border: 'var(--shape-line) solid var(--border, #E4E4E7)', borderRadius: 'var(--shape-card)', padding: 20, background: 'var(--card, #fff)', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: 4, textAlign: 'center' }}>{form.title}</div>
      {form.description && <p style={{ fontSize: '0.85rem', color: 'var(--muted-fg, #71717A)', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.9 }}>{form.description}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(form.fields || []).map(f => {
          const v = answers[f.id] ?? '';
          const req = f.required ? ' *' : '';
          if (f.type === 'textarea') return <div key={f.id}><label style={lbl}>{f.label}{req}</label><textarea value={v} onChange={e => set(f.id, e.target.value)} placeholder={f.placeholder || ''} rows={4} required={!!f.required} style={{ ...inStyle, resize: 'vertical', lineHeight: 1.9 }} /></div>;
          if (f.type === 'number') return <div key={f.id}><label style={lbl}>{f.label}{req}</label><input value={v} onChange={e => set(f.id, e.target.value)} placeholder={f.placeholder || ''} required={!!f.required} inputMode="numeric" style={inStyle} dir="ltr" /></div>;
          if (f.type === 'phone') return <div key={f.id}><label style={lbl}>{f.label}{req}</label><input value={v} onChange={e => set(f.id, e.target.value.replace(/[^0-9]/g, ''))} placeholder={f.placeholder || '09123456789'} required={!!f.required} inputMode="tel" style={{ ...inStyle, textAlign: 'left' }} dir="ltr" /></div>;
          if (f.type === 'email') return <div key={f.id}><label style={lbl}>{f.label}{req}</label><input type="email" value={v} onChange={e => set(f.id, e.target.value)} placeholder={f.placeholder || ''} required={!!f.required} style={{ ...inStyle, textAlign: 'left' }} dir="ltr" /></div>;
          if (f.type === 'date') return <div key={f.id}><label style={lbl}>{f.label}{req}</label><input type="date" value={v} onChange={e => set(f.id, e.target.value)} required={!!f.required} style={inStyle} /></div>;
          if (f.type === 'select') return (
            <div key={f.id}><label style={lbl}>{f.label}{req}</label>
              <select value={v} onChange={e => set(f.id, e.target.value)} required={!!f.required} style={{ ...inStyle, cursor: 'pointer' }}>
                <option value="">انتخاب کنید...</option>
                {(f.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
              </select>
            </div>
          );
          if (f.type === 'radio') return (
            <div key={f.id}><label style={lbl}>{f.label}{req}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(f.options || []).map((o, i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.86rem', cursor: 'pointer' }}>
                    <input type="radio" name={`wf-${form.id}-${f.id}`} checked={v === o} onChange={() => set(f.id, o)} required={!!f.required && !v} style={{ accentColor: 'var(--primary)', width: 16, height: 16 }} /> {o}
                  </label>
                ))}
              </div>
            </div>
          );
          if (f.type === 'checkbox') return (
            <div key={f.id}><label style={lbl}>{f.label}{req}</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(f.options || []).map((o, i) => {
                  const arr = Array.isArray(v) ? v : [];
                  return (
                    <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.86rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={arr.includes(o)} onChange={e => set(f.id, e.target.checked ? [...arr, o] : arr.filter(x => x !== o))} style={{ accentColor: 'var(--primary)', width: 16, height: 16 }} /> {o}
                    </label>
                  );
                })}
              </div>
            </div>
          );
          return <div key={f.id}><label style={lbl}>{f.label}{req}</label><input value={v} onChange={e => set(f.id, e.target.value)} placeholder={f.placeholder || ''} required={!!f.required} style={inStyle} /></div>;
        })}
        <button type="submit" disabled={busy}
          style={{ padding: '13px', border: 'none', background: 'var(--primary, #18181B)', color: 'var(--on-primary, #fff)', minHeight: 'var(--shape-btn-h)', borderRadius: 'var(--shape-btn)', fontWeight: 700, fontSize: '0.92rem', cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'در حال ارسال...' : (form.settings?.submit_text || 'ارسال')}
        </button>
      </div>
    </form>
  );
}

/** Current post/product comments placement — needs data.post or data.product context */
export function WidgetPostComments({ settings = {}, ctx = {} }) {
  const s = settings;
  const post = ctx.post;
  const product = ctx.product;
  if (post) return <CommentsBox type="post" id={post.id} title={(s.sectionTitle || s.title)} />;
  if (product) return <CommentsBox type="product" id={product.id} title={(s.sectionTitle || s.title)} />;
  return null;
}


/* ================= ویجت‌های سبک دیجی‌کالا ================= */

const imgUrl = (path) => (!path ? '' : /^https?:\/\//.test(path) ? path : `${STORAGE_BASE}/${String(path).replace(/^\/+/, '')}`);

const scrollRowCss = (gap = 12) => ({
  display: 'flex', gap, overflowX: 'auto', scrollSnapType: 'x mandatory',
  padding: '4px 2px 14px', margin: '0 -2px', scrollbarWidth: 'thin',
});

/** کارت کوچک محصول — چینش ثابت: تصویر، عنوان ۲ خطی، ردیف قیمت/تخفیف، دکمه. */
function MiniProductCard({ p, width = 212, st }) {
  const pi = priceInfo(p);
  const out = (p.stock ?? 0) < 1;
  const src = imgUrl(p.image_path || p.media?.[0]?.path || p.customization_data?.image);
  return (
    <a href={`/products/${p.id}`} style={{
      flex: `0 0 ${width}px`, width, scrollSnapAlign: 'start', textDecoration: 'none', color: 'inherit',
      background: 'var(--card)', border: 'var(--shape-line) solid var(--border)', borderRadius: 'var(--shape-card)',
      overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 18px 36px -18px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
      <div style={{ position: 'relative', aspectRatio: '1 / 1', background: 'var(--muted)' }}>
        {src && <img src={src} alt={p.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        {pi.hasDiscount && (
          <span style={{ position: 'absolute', top: 8, right: 8, background: 'var(--primary)', color: 'var(--on-primary)', fontSize: '0.68rem', fontWeight: 800, borderRadius: 'var(--shape-btn)', padding: '3px 9px' }}>
            ٪{pi.pct.toLocaleString('fa-IR')}
          </span>
        )}
        {out && <span style={{ position: 'absolute', inset: 0, background: 'color-mix(in srgb, var(--bg) 70%, transparent)', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: '0.85rem' }}>ناموجود</span>}
      </div>
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '0.86rem', lineHeight: 1.7, minHeight: '2.8em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.name}</div>
        <div style={{ marginTop: 'auto', paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {pi.hasDiscount && <span style={{ color: 'var(--muted-fg)', fontSize: '0.68rem', textDecoration: 'line-through' }}>{pi.base.toLocaleString('fa-IR')}</span>}
            <span style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--fg)', whiteSpace: 'nowrap' }}>{pi.price.toLocaleString('fa-IR')} تومان</span>
          </div>
          <span style={{ width: 32, height: 32, borderRadius: 'var(--shape-btn)', background: 'var(--primary)', color: 'var(--on-primary)', display: 'grid', placeItems: 'center', flexShrink: 0 }} aria-hidden>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
          </span>
        </div>
      </div>
    </a>
  );
}

/** ویترین افقی محصولات (سبک دیجی‌کالا) — اسکرول افقی کارت‌های هم‌اندازه با تخفیف. */
export function WidgetProductScroll({ settings: s = {}, data }) {
  const src = s.source === 'featured' ? (data?.featured || []) : (data?.products || []);
  const items = src.slice(0, Math.max(2, Number(s.limit) || 12));
  if (!items.length) return null;
  return (
    <div>
      {(s.sectionTitle || s.title || s.showHeader !== false) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: s.titleSize ? `${s.titleSize}px` : 'clamp(1.15rem, 3vw, 1.5rem)', color: s.titleColor || 'var(--fg)' }}>
            {s.sectionTitle || s.title || 'محصولات فروشگاه'}
          </h2>
          <a href={s.link || '/products'} style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap', ...partStyle(s.style, 'button') }}>{s.linkLabel || 'مشاهده همه'}</a>
        </div>
      )}
      <div style={scrollRowCss(12)}>
        {items.map(p => <MiniProductCard key={p.id} p={p} width={s.cardWidth ? Number(s.cardWidth) : 212} />)}
      </div>
    </div>
  );
}

/** نوار دسته‌بندی‌ها (سبک دیجی‌کالا) — چیپ‌های اسکرولی با تصویر و عنوان. */
export function WidgetCategoryStrip({ settings: s = {}, data }) {
  const cats = (data?.categories || []).slice(0, Math.max(3, Number(s.limit) || 16));
  if (!cats.length) return null;
  return (
    <div>
      {(s.sectionTitle || s.title || s.showHeader !== false) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: s.titleSize ? `${s.titleSize}px` : 'clamp(1.15rem, 3vw, 1.5rem)', color: s.titleColor || 'var(--fg)' }}>
            {s.sectionTitle || s.title || 'خرید بر اساس دسته‌بندی'}
          </h2>
          <a href={s.link || '/products'} style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap', ...partStyle(s.style, 'button') }}>{s.linkLabel || 'مشاهده همه'}</a>
        </div>
      )}
      <div style={scrollRowCss(10)}>
        {cats.map(c => {
          const img = imgUrl(c.image_path || c.image);
          const count = Array.isArray(c.products) ? c.products.length : null;
          return (
            <a key={c.id} href={`/products?category=${c.id}`} style={{
              flex: `0 0 ${s.cardWidth ? Number(s.cardWidth) : 128}px`, scrollSnapAlign: 'start', textDecoration: 'none', color: 'inherit',
              background: s.variant === 'plain' ? 'transparent' : 'color-mix(in srgb, var(--surface, #fff) 92%, var(--primary) 8%)', border: `var(--shape-line) solid ${s.variant === 'plain' ? 'transparent' : 'color-mix(in srgb, var(--primary) 18%, var(--border, #e5e7eb))'}`, borderRadius: s.variant === 'circles' ? '999px' : 'var(--shape-card)',
              padding: '14px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 32px -16px rgba(0,0,0,0.28)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
              <span style={{ width: s.variant === 'circles' ? 88 : 64, height: s.variant === 'circles' ? 88 : 64, borderRadius: s.variant === 'circles' ? '999px' : 'var(--shape-btn)', overflow: 'hidden', background: 'color-mix(in srgb, var(--primary) 12%, transparent)', display: 'grid', placeItems: 'center', border: s.variant === 'circles' ? '3px solid color-mix(in srgb, var(--primary) 30%, transparent)' : 'none' }}>
                {img ? <img src={img} alt={c.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontWeight: 900, fontSize: '1.35rem', color: 'var(--primary)' }}>{(c.name || '؟').trim().charAt(0)}</span>}
              </span>
              <span style={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.6, minHeight: '2.6em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{c.name}</span>
              {count !== null && <span style={{ fontSize: '0.68rem', color: 'var(--muted-fg)' }}>{count.toLocaleString('fa-IR')} کالا</span>}
            </a>
          );
        })}
      </div>
    </div>
  );
}

/**
 * استودیو اسلایدر ساز (Slider Studio) — اسلایدر محصولات/دسته‌بندی/سفارشی با چند سبک نمایش.
 * حالت‌ها: classic | coverflow | fade | ticker | story | thumbs
 */
export function WidgetSliderPro({ settings: s = {}, data }) {
  const mode = s.sliderMode || 'classic';
  const [idx, setIdx] = React.useState(0);
  const timer = React.useRef(null);

  // منبع آیتم‌ها
  let items = [];
  if (s.content === 'categories') {
    items = (data?.categories || []).slice(0, Math.max(2, Number(s.limit) || 12)).map(c => ({
      id: c.id, title: c.name, image: c.image_path || c.image, link: `/products?category=${c.id}`, count: Array.isArray(c.products) ? c.products.length : null,
    }));
  } else if (s.content === 'custom' && Array.isArray(s.slides) && s.slides.length) {
    items = s.slides.slice(0, 12).map((sl, i) => ({ id: i, title: sl.title, text: sl.text, image: sl.image, link: sl.link, buttonText: sl.buttonText }));
  } else {
    const src = s.source === 'featured' ? (data?.featured || []) : (data?.products || []);
    items = src.slice(0, Math.max(2, Number(s.limit) || 12)).map(p => ({ id: p.id, title: p.name, image: p.image_path || p.image, link: `/products/${p.id}`, price: p.price, base: (p.customization_data || {}).base_price }));
  }
  const n = items.length;
  const delay = Math.max(1200, Number(s.autoplayDelay) || 3800);
  React.useEffect(() => {
    if (!n || s.autoplay === false || mode === 'ticker') return undefined;
    timer.current = setInterval(() => setIdx(i => (i + 1) % n), delay);
    return () => clearInterval(timer.current);
  }, [n, delay, s.autoplay, mode]);

  if (!items.length) return null;

  const go = d => setIdx(i => (i + d + n) % n);
  const radius = (s.radius === undefined || s.radius === null || s.radius === '') ? 'var(--shape-card)' : `${s.radius}px`;
  const gap = Math.max(6, Number(s.gap) || 14);
  const cardW = Math.max(160, Number(s.cardWidth) || 240);
  const height = Math.max(160, Number(s.height) || (mode === 'story' ? 360 : 300));

  const header = (s.showHeader !== false) && (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 }}>
      <h2 style={{ margin: 0, fontWeight: 800, fontSize: s.titleSize ? `${s.titleSize}px` : 'clamp(1.15rem, 3vw, 1.5rem)', color: s.titleColor || 'var(--fg)' }}>
        {s.sectionTitle || s.title || 'ویترین محصولات'}
      </h2>
      {s.link && <a href={s.link} style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none', whiteSpace: 'nowrap', ...partStyle(s.style, 'button') }}>{s.linkLabel || 'مشاهده همه'}</a>}
    </div>
  );

  // ---------- حالت story: اسلاید تمام‌عرض با متن روی تصویر ----------
  if (mode === 'story') {
    const cur = items[idx % n];
    return (
      <div>
        {header}
        <div style={{ position: 'relative', height, borderRadius: radius, overflow: 'hidden', background: 'var(--card)' }}>
          {items.map((it, i) => (
            <a key={it.id ?? i} href={it.link || '#'} style={{
              position: 'absolute', inset: 0, opacity: i === (idx % n) ? 1 : 0, transition: 'opacity 0.7s ease', textDecoration: 'none',
            }}>
              <ImageFallback image={it.image} title={it.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: s.overlay !== false ? 'linear-gradient(90deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)' : 'none' }} />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10, padding: 'clamp(18px, 5vw, 52px)', maxWidth: '62%' }}>
                {it.text && <span style={{ color: '#fff', opacity: 0.9, fontWeight: 700, fontSize: '0.85rem' }}>{it.text}</span>}
                <strong style={{ color: '#fff', fontSize: s.titleSize ? `${s.titleSize + 8}px` : 'clamp(1.35rem, 4vw, 2.4rem)', lineHeight: 1.5 }}>{it.title}</strong>
                {(it.buttonText || s.buttonText) && <span style={{ alignSelf: 'flex-start', marginTop: 8, background: 'var(--primary)', color: 'var(--primary-contrast, #fff)', padding: '11px 24px', borderRadius: 'var(--shape-btn)', fontWeight: 800, fontSize: '0.9rem' }}>{it.buttonText || s.buttonText}</span>}
              </div>
            </a>
          ))}
          {s.arrows !== false && <>
            <button onClick={() => go(-1)} aria-label="قبلی" style={arrowBtn('left', height)}>‹</button>
            <button onClick={() => go(1)} aria-label="بعدی" style={arrowBtn('right', height)}>›</button>
          </>}
          {s.dots !== false && <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 7 }}>
            {items.map((_, i) => <button key={i} aria-label={`اسلاید ${i + 1}`} onClick={() => setIdx(i)} style={{ width: i === (idx % n) ? 26 : 9, height: 9, borderRadius: 99, border: 'none', background: i === (idx % n) ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'width 0.25s' }} />)}
          </div>}
        </div>
      </div>
    );
  }

  // ---------- حالت ticker: نوار پیوسته ----------
  if (mode === 'ticker') {
    const row = [...items, ...items];
    return (
      <div>
        {header}
        <div style={{ overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)' }}>
          <div className="hb-ticker-row" style={{ display: 'flex', gap, width: 'max-content', animation: `hbTicker ${Math.max(12, Number(s.speed) || 32)}s linear infinite` }}>
            {row.map((it, i) => <MiniProductCard key={`${it.id}-${i}`} p={it} width={cardW} />)}
          </div>
        </div>
      </div>
    );
  }

  // ---------- حالت fade: یک اسلاید محوشونده بزرگ ----------
  if (mode === 'fade') {
    return (
      <div>
        {header}
        <div style={{ position: 'relative', height, borderRadius: radius, overflow: 'hidden' }}>
          {items.map((it, i) => (
            <a key={it.id ?? i} href={it.link || '#'} style={{ position: 'absolute', inset: 0, opacity: i === (idx % n) ? 1 : 0, transition: 'opacity 0.8s ease', display: 'flex', alignItems: 'flex-end', textDecoration: 'none' }}>
              <ImageFallback image={it.image} title={it.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'relative', padding: 'clamp(14px, 3vw, 30px)', background: 'linear-gradient(0deg, rgba(0,0,0,0.55), transparent)', width: '100%' }}>
                <strong style={{ color: '#fff', fontSize: '1.15rem', display: 'block' }}>{it.title}</strong>
                {it.price && <span style={{ color: '#fff', opacity: 0.9, fontSize: '0.9rem' }}>{it.price.toLocaleString('fa-IR')} تومان</span>}
              </div>
            </a>
          ))}
          {s.arrows !== false && <>
            <button onClick={() => go(-1)} aria-label="قبلی" style={arrowBtn('left', height)}>‹</button>
            <button onClick={() => go(1)} aria-label="بعدی" style={arrowBtn('right', height)}>›</button>
          </>}
        </div>
      </div>
    );
  }

  // ---------- کلاسیک / کاورفلو / thumbs ----------
  const shift = idx % Math.max(1, n);
  const trackStyle = mode === 'coverflow'
    ? { display: 'flex', gap, transition: 'transform 0.5s cubic-bezier(.2,.8,.2,1)', transform: `translateX(calc(50% - ${(shift + 0.5) * (cardW + gap)}px))` }
    : { display: 'flex', gap, overflowX: 'auto', scrollSnapType: 'x mandatory', scrollbarWidth: 'none' };

  return (
    <div>
      {header}
      {mode === 'thumbs' && (
        <div style={{ borderRadius: radius, overflow: 'hidden', height, marginBottom: 12, position: 'relative' }}>
          <a href={items[shift]?.link || '#'}>
            <ImageFallback image={items[shift]?.image} title={items[shift]?.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '18px 22px', background: 'linear-gradient(0deg, rgba(0,0,0,0.55), transparent)', color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>{items[shift]?.title}</div>
          </a>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={trackStyle}>
          {items.map((it, i) => {
            const active = mode === 'coverflow' && i === shift;
            return (
              <div key={it.id ?? i} style={{
                flex: `0 0 ${mode === 'thumbs' ? Math.round(cardW * 0.42) : cardW}px`,
                scrollSnapAlign: 'center',
                transform: active ? 'scale(1.06)' : mode === 'coverflow' ? 'scale(0.9)' : 'none',
                opacity: mode === 'coverflow' && !active ? 0.55 : 1,
                transition: 'transform 0.45s ease, opacity 0.45s ease',
              }}>
                <MiniProductCard p={it} width={mode === 'thumbs' ? Math.round(cardW * 0.42) : cardW} />
              </div>
            );
          })}
        </div>
        {mode !== 'thumbs' && s.arrows !== false && <>
          <button onClick={() => go(-1)} aria-label="قبلی" style={{ ...arrowBtn('left', 0), top: '50%', transform: 'translateY(-50%)' }}>‹</button>
          <button onClick={() => go(1)} aria-label="بعدی" style={{ ...arrowBtn('right', 0), top: '50%', transform: 'translateY(-50%)' }}>›</button>
        </>}
      </div>
      {s.dots !== false && mode !== 'ticker' && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 7, marginTop: 12 }}>
          {items.slice(0, n).map((_, i) => <button key={i} aria-label={`اسلاید ${i + 1}`} onClick={() => setIdx(i)} style={{ width: i === (idx % n) ? 26 : 9, height: 9, borderRadius: 99, border: 'none', background: i === (idx % n) ? 'var(--primary)' : 'color-mix(in srgb, var(--fg) 22%, transparent)', cursor: 'pointer', transition: 'width 0.25s' }} />)}
        </div>
      )}
    </div>
  );
}

function arrowBtn(side, height) {
  return {
    position: 'absolute', [side]: 8, top: height ? height / 2 : undefined, transform: height ? 'translateY(-50%)' : undefined,
    width: 42, height: 42, borderRadius: 99, border: 'none', cursor: 'pointer', zIndex: 3,
    background: 'rgba(255,255,255,0.92)', color: '#111', fontSize: '1.5rem', lineHeight: 1,
    boxShadow: '0 10px 24px -12px rgba(0,0,0,0.35)', display: 'grid', placeItems: 'center',
  };
}

/** پیشنهاد ویژه (سبک دیجی‌کالا) — پنل تبلیغاتی + کارت‌های تخفیف‌دار. */
/** پیشنهاد ویژه (سبک دیجی‌کالا) — پنل تبلیغاتی + کارت‌های تخفیف‌دار. */
export function WidgetDealRow({ settings: s = {}, data }) {
  const all = data?.products || [];
  const discounted = all.filter(p => priceInfo(p).hasDiscount);
  const items = (discounted.length ? discounted : all).slice(0, Math.max(2, Number(s.limit) || 8));
  if (!items.length) return null;
  return (
    <div style={{
      background: 'linear-gradient(150deg, var(--primary), color-mix(in srgb, var(--primary) 68%, var(--bg)))',
      borderRadius: 'var(--shape-card)', padding: 14,
    }}>
      <div style={scrollRowCss(8)}>
        {/* پنل پیشنهاد — اولین کارت ردیف (دسکتاپ کامل) */}
        <div className="hide-mobile" style={{
          flex: '0 0 230px', scrollSnapAlign: 'start',
          color: 'var(--on-primary)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 10, padding: '8px 6px',
        }}>
          <span style={{ alignSelf: 'flex-start', background: 'color-mix(in srgb, var(--on-primary) 18%, transparent)', borderRadius: 'var(--shape-btn)', padding: '4px 12px', fontSize: '0.72rem', fontWeight: 800 }}>
            {s.badge || 'پیشنهاد شگفت‌انگیز'}
          </span>
          <div style={{ fontWeight: 900, fontSize: s.titleSize ? `${s.titleSize}px` : 'clamp(1.2rem, 2.5vw, 1.6rem)', lineHeight: 1.5 }}>
            {s.sectionTitle || s.title || 'تخفیف‌های محدود امروز'}
          </div>
          {s.desc && <div style={{ fontSize: '0.85rem', lineHeight: 1.9, opacity: 0.92 }}>{s.desc}</div>}
          {(s.buttonText || s.link) && (
            <a href={s.link || '/products'} style={{
              alignSelf: 'flex-start', marginTop: 4, background: 'var(--on-primary)', color: 'var(--primary)',
              borderRadius: 'var(--btn-radius-i, var(--shape-btn))', minHeight: 'var(--shape-btn-h)', padding: '9px 22px',
              fontWeight: 'var(--btn-weight, 800)', fontSize: '0.88rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
              ...partStyle(s.style, 'button'),
            }}>{s.buttonText || 'مشاهده تخفیف‌ها'}</a>
          )}
        </div>
        {/* پنل فشرده — اول ردیف، فقط موبایل */}
        <a href={s.link || '/products'} className="show-mobile-only" style={{
          flex: '0 0 150px', scrollSnapAlign: 'start', textDecoration: 'none',
          color: 'var(--on-primary)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8, padding: '8px 6px',
        }}>
          <span style={{ background: 'color-mix(in srgb, var(--on-primary) 18%, transparent)', borderRadius: 'var(--shape-btn)', padding: '4px 10px', fontSize: '0.68rem', fontWeight: 800, alignSelf: 'flex-start' }}>
            {s.badge || 'پیشنهاد شگفت‌انگیز'}
          </span>
          <span style={{ fontWeight: 900, fontSize: '0.95rem', lineHeight: 1.7 }}>{s.sectionTitle || s.title || 'تخفیف‌ها'}</span>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.92 }}>مشاهده همه ←</span>
        </a>
        {items.map(p => <MiniProductCard key={p.id} p={p} st={s.style} width={s.cardWidth ? Number(s.cardWidth) : 196} />)}
      </div>
    </div>
  );
}

/**
 * هیرو اسلایدر تمام‌عرض (مدل mr-silver): پس‌زمینه گرادیانی چرخان + هاله نور،
 * ستون متن راست (لوگو، لیبل ساده، تیتر کرم درشت، دکمه روشن با فلش، ناوبری فلش+خط)،
 * عکس محصول بزرگ چپ با حلقه‌های تزئینی دورش.
 * settings: slides[{image,logo,label,title,subtitle,buttonText,buttonLink,glow,bgFrom,bgTo,textColor}]
 * + height, interval, showDots, showArrows, showRings, overlayOpacity, radius, bordered, titleSize
 */
export function WidgetHeroSlider({ settings = {} }) {
  const s = settings;
  const slides = (s.slides || []).filter(sl => sl.image || sl.title);
  const [i, setI] = useState(0);
  const interval = Math.max(2000, Number(s.interval) || 6000);
  const height = Math.max(320, Number(s.height) || 560);
  const showDots = s.showDots !== false;
  const showArrows = s.showArrows !== false;
  const showRings = s.showRings !== false;
  const align = ['right', 'left', 'center'].includes(s.textSide) ? s.textSide : 'right';
  const overlayOp = Math.min(Math.max(Number(s.overlayOpacity ?? 0.3), 0), 0.8);
  const radius = s.radius ?? 0;
  const bordered = s.bordered !== false;
  const cream = '#F8F3EA';
  const m = useIsMobile();
  const swipeX = React.useRef(null);
  const swipeStart = x => { swipeX.current = x; };
  const swipeEnd = x => {
    if (swipeX.current === null) return;
    const dx = x - swipeX.current;
    swipeX.current = null;
    if (Math.abs(dx) < 50) return;
    setI(p => (p + (dx < 0 ? slides.length - 1 : 1)) % slides.length);
  };

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setI(p => (p + slides.length) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!slides.length) return null;
  const go = d => setI(p => (p + d + slides.length) % slides.length);
  const cur = slides[i] || {};
  const bgFrom = cur.bgFrom || '#0B3A30';
  const bgTo = cur.bgTo || '#1F7A62';
  const glow = cur.glow || 'rgba(120,255,200,0.22)';
  const textColor = cur.textColor || cream;

  return (
    <div style={{
      position: 'relative', width: '100%', minHeight: height, overflow: 'hidden',
      borderRadius: radius, border: bordered ? 'var(--shape-line) solid var(--border)' : 'none',
      direction: 'rtl', background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})`,
      transition: 'background 0.8s ease',
      onTouchStart: e => swipeStart(e.touches[0].clientX),
      onTouchEnd: e => swipeEnd(e.changedTouches[0].clientX),
      onMouseDown: e => swipeStart(e.clientX),
      onMouseUp: e => swipeEnd(e.clientX),
      onMouseLeave: e => { if (swipeX.current !== null) swipeEnd(e.clientX); },
    }}>
      {/* هاله نور */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `radial-gradient(60% 90% at 40% 50%, ${glow}, transparent 70%)` }} />
      {/* لایه تیره برای خوانایی متن */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: `linear-gradient(90deg, rgba(0,0,0,${overlayOp}) 0%, rgba(0,0,0,${overlayOp * 0.35}) 55%, transparent 100%)` }} />
      {/* اسلایدها */}
      {slides.map((sl, idx) => {
        const on = idx === i;
        return (
          <div key={idx} style={{ position: 'absolute', inset: 0, opacity: on ? 1 : 0, transition: 'opacity 0.8s ease', pointerEvents: on ? 'auto' : 'none' }}>
            <div style={{
              position: 'relative', height: '100%', minHeight: m ? 640 : height, maxWidth: 1200, margin: '0 auto',
              display: 'flex', alignItems: 'center', gap: m ? 6 : 24, padding: m ? '20px 16px 26px' : '28px clamp(16px,4vw,48px)',
              flexDirection: (m || align === 'center') ? 'column' : 'row', justifyContent: (m || align === 'center') ? 'center' : 'space-between',
            }}>
              <div style={{ flex: 1, minWidth: 0, maxWidth: (m || align === 'center') ? 720 : 460, color: sl.textColor || textColor, textAlign: (m || align === 'center') ? 'center' : 'right' }}>
                {sl.logo && !m && (
                  <img src={resolveSrc(sl.logo)} alt="" style={{ width: 104, height: 'auto', display: 'block', margin: '0 0 10px auto' }} />
                )}
                {sl.label && !m && (
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, opacity: 0.9, marginBottom: 10 }}>{sl.label}</div>
                )}
                {sl.title && (
                  <div style={{ fontWeight: 800, fontSize: s.titleSize ? `${s.titleSize}px` : (m ? 'clamp(1.3rem, 6.5vw, 1.6rem)' : 'clamp(1.7rem, 4.5vw, 2.6rem)'), lineHeight: 1.6, margin: '0 0 10px', textShadow: '0 2px 14px rgba(0,0,0,0.35)', whiteSpace: 'pre-line' }}>
                    {sl.title}
                  </div>
                )}
                {sl.subtitle && (
                  <div style={{ fontSize: m ? '0.85rem' : '0.95rem', opacity: 0.9, lineHeight: 2, marginBottom: 18 }}>{sl.subtitle}</div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: (m || align === 'center') ? 'center' : 'flex-start' }}>
                  {sl.buttonText && (
                    <a href={sl.buttonLink || '/products'} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', background: cream, color: '#1a1612', fontWeight: 700, fontSize: '1rem', padding: m ? '12px 40px' : '14px 34px', borderRadius: 999, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
                      {sl.buttonText}
                      <ChevronLeft size={20} />
                    </a>
                  )}
                  {sl.button2Text && (
                    <a href={sl.button2Link || '/products'} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: '#fff', fontWeight: 700, fontSize: '0.95rem', padding: '13px 30px', borderRadius: 999, border: '1.5px solid rgba(255,255,255,0.6)' }}>
                      {sl.button2Text}
                    </a>
                  )}
                </div>
                {(showArrows || showDots) && slides.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: m ? 16 : 22, justifyContent: (m || align === 'center') ? 'center' : 'flex-start' }}>
                    {showArrows && (
                      <button onClick={() => go(1)} aria-label="قبلی" style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ChevronRight size={20} />
                      </button>
                    )}
                    {showDots && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {slides.map((_, d) => (
                          <button key={d} onClick={() => setI(d)} aria-label={`اسلاید ${d + 1}`}
                            style={{ width: 26, height: 44, border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ display: 'block', width: 4, height: d === i ? 26 : 16, borderRadius: 3, background: d === i ? '#fff' : 'rgba(255,255,255,0.45)', transition: 'height 0.3s' }} />
                          </button>
                        ))}
                      </div>
                    )}
                    {showArrows && (
                      <button onClick={() => go(-1)} aria-label="بعدی" style={{ width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.10)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ChevronLeft size={20} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {(m || align !== 'center') && sl.image && (
                <div style={m
                  ? { position: 'relative', width: 210, maxWidth: '62vw', display: 'flex', alignItems: 'center', justifyContent: 'center', order: -1 }
                  : { position: 'relative', flexShrink: 0, width: 'min(40vw, 400px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {showRings && (
                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(m ? [240, 175, 120] : [430, 320, 215]).map(r => (
                        <div key={r} style={{ position: 'absolute', width: r, height: r, maxWidth: '100%', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.20)' }} />
                      ))}
                    </div>
                  )}
                  <img src={resolveSrc(sl.image)} alt={sl.title || ''} loading={idx === 0 ? 'eager' : 'lazy'} draggable={false}
                    style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block', borderRadius: '50%', filter: 'drop-shadow(0 24px 48px rgba(0,0,0,0.45))' }} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

